import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import React, { useState, useMemo, useCallback } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  validateRow,
  validateRowByMappings,
} from "../helpers/validationHelpers";
import { Delete } from "@mui/icons-material";
import { IHeaderMapping } from "../CreateAudienceRightPanel.types";

interface EditableCell {
  rowIndex: number;
  field: string;
  value: string;
}

type IStep2ExtractHeaders = {
  headers: string[];
  data: Record<string, any>[];
  mappings?: IHeaderMapping[];
  onDataUpdate?: (updatedData: Record<string, any>[]) => void;
  onValidationStatsUpdate?: (stats: {
    total: number;
    valid: number;
    issues: number;
  }) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const Step2ExtractHeaders: React.FC<IStep2ExtractHeaders> = ({
  headers,
  data,
  mappings,
  onDataUpdate,
  onValidationStatsUpdate,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [editingCell, setEditingCell] = useState<EditableCell | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [localData, setLocalData] = useState<Record<string, any>[]>(data);

  // const isEmptyRow = useCallback((row: Record<string, any>): boolean => {
  //   return Object.values(row).every(
  //     (value) => !value || value === "" || value === "Click to edit",
  //   );
  // }, []);

  const isEmptyRow = useCallback((row: Record<string, any>): boolean => {
    return Object.values(row).every((value) => !value || value === "");
  }, []);

  const filteredData = useMemo(() => {
    return localData.filter((row) => !isEmptyRow(row));
  }, [localData, isEmptyRow]);

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const validationResults = useMemo(() => {
    return filteredData.map((row, index) => ({
      rowIndex: index,
      validations:
        mappings && mappings.length > 0
          ? validateRowByMappings(row, mappings)
          : validateRow(row, headers),
      hasErrors:
        mappings && mappings.length > 0
          ? validateRowByMappings(row, mappings).some((v) => !v.isValid)
          : validateRow(row, headers).some((v) => !v.isValid),
    }));
  }, [filteredData, headers, mappings]);

  const validationStats = useMemo(() => {
    const total = filteredData.length;
    const valid = validationResults.filter(
      (result) => !result.hasErrors,
    ).length;
    const issues = total - valid;

    return { total, valid, issues };
  }, [filteredData.length, validationResults]);

  const getRowValidationStatus = useCallback(
    (rowIndex: number) => {
      const result = validationResults[rowIndex];
      if (!result) return { isValid: true, message: "No validation data" };

      const hasErrors = result.hasErrors;
      const errorMessages = result.validations
        .filter((v) => !v.isValid)
        .map((v) => v.message)
        .join("; ");

      return {
        isValid: !hasErrors,
        message: hasErrors ? errorMessages : "All validations passed",
      };
    },
    [validationResults],
  );

  const handleDelete = useCallback(
    (filteredRowIndex: number) => {
      const rowToDelete = filteredData[filteredRowIndex];
      if (!rowToDelete) return;

      const originalIndex = localData.findIndex(
        (row) => JSON.stringify(row) === JSON.stringify(rowToDelete),
      );

      if (originalIndex === -1) return;

      const updatedData = localData.filter(
        (_, index) => index !== originalIndex,
      );

      setLocalData(updatedData);
      onDataUpdate?.(updatedData);
    },
    [localData, onDataUpdate, filteredData],
  );

  const handleCellEdit = useCallback(
    (rowIndex: number, field: string, currentValue: any) => {
      setEditingCell({
        rowIndex,
        field,
        value: currentValue?.toString() || "",
      });
      setEditValue(currentValue?.toString() || "");
    },
    [],
  );

  const handleCellSave = useCallback(() => {
    if (!editingCell) return;

    const { rowIndex, field } = editingCell;
    const updatedData = [...localData];
    updatedData[rowIndex] = {
      ...updatedData[rowIndex],
      [field]: editValue,
    };

    setLocalData(updatedData);
    onDataUpdate?.(updatedData);
    setEditingCell(null);
    setEditValue("");
  }, [editingCell, editValue, localData, onDataUpdate]);

  const handleCellCancel = useCallback(() => {
    setEditingCell(null);
    setEditValue("");
  }, []);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleCellSave();
      } else if (event.key === "Escape") {
        handleCellCancel();
      }
    },
    [handleCellSave, handleCellCancel],
  );

  React.useEffect(() => {
    onValidationStatsUpdate?.(validationStats);
  }, [validationStats, onValidationStatsUpdate]);

  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  return (
    <TableContainer component={Paper} elevation={1}>
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: "primary.main" }}>
            {/* <TableCell
              sx={{
                color: "#fff",
                fontSize: "0.95rem",
                fontWeight: 500,
                width: 50,
                position: "sticky",
                left: 0,
                zIndex: 1,
                backgroundColor: "primary.main",
              }}
            >
              Actions
            </TableCell> */}
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{
                  color: "#fff",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  minWidth: 120,
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  backgroundColor: "primary.main",
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((row, localRowIndex) => {
            const actualRowIndex = startIndex + localRowIndex;
            const validationStatus = getRowValidationStatus(actualRowIndex);
            const isRowValid = validationStatus.isValid;

            return (
              <TableRow
                key={actualRowIndex}
                sx={{
                  backgroundColor: isRowValid ? "inherit" : "#fff3e0",
                  "&:hover": {
                    backgroundColor: isRowValid ? "#f5f5f5" : "#ffecb3",
                  },
                  borderLeft: isRowValid ? "none" : "4px solid #ff9800",
                }}
              >
                {/* Actions Column */}
                {/* <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: isRowValid ? "inherit" : "#fff3e0",
                    borderRight: "2px solid #e0e0e0",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Delete
                      onClick={() => handleDelete(actualRowIndex)}
                      sx={{ color: "error.main", fontSize: 20 }}
                    />

                    {isRowValid ? (
                      <CheckCircleIcon
                        sx={{ color: "success.main", fontSize: 20 }}
                      />
                    ) : (
                      <Tooltip title={validationStatus.message}>
                        <InfoIcon
                          sx={{ color: "warning.main", fontSize: 20 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell> */}

                {/* Data Columns */}
                {headers.map((header) => {
                  const isEditing =
                    editingCell?.rowIndex === actualRowIndex &&
                    editingCell?.field === header;
                  const cellValue = row[header]?.toString() || "";

                  return (
                    <TableCell
                      key={`${actualRowIndex}-${header}`}
                      sx={{
                        position: "relative",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                      // onClick={() =>
                      //   !isEditing &&
                      //   handleCellEdit(actualRowIndex, header, cellValue)
                      // }
                    >
                      {isEditing ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <TextField
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            size="small"
                            autoFocus
                            fullWidth
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={handleCellSave}
                            sx={{ color: "success.main" }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCellCancel}
                            sx={{ color: "error.main" }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              wordBreak: "break-word",
                              color: cellValue
                                ? "text.primary"
                                : "text.secondary",
                            }}
                          >
                            {/* {cellValue || "Click to edit"} */}
                            {cellValue || "--"}
                          </Typography>
                          {/* <EditIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              opacity: 0.7,
                              "&:hover": {
                                opacity: 1,
                                color: "primary.main",
                              },
                            }}
                          /> */}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
